import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '../../shared/components/Common';
import Icon from '../../shared/components/Icons';
import { FlowBand } from '../../shared/booking/FlowParts';
import { formatPrice } from '../../shared/data/common';
import { reviewTransfer } from '../../api/transfers';
import { durationLabel, journeyLabel, journeyFromType, parseTransferQuery, passengersLabel, splitDateTime, transferQueryString, whenLabel } from './searchModel';
import { cancellationLines, freeCancellation } from './data';
import { VehicleImage } from './TransferList';

/** Pickup → drop with times, from the API's journey. */
export function RouteBox({ journey }) {
  const pickup = splitDateTime(journey.pickupDateTime);
  const back = journey.returnDateTime && splitDateTime(journey.returnDateTime);
  return (
    <div className="route-box">
      <div><span className="dot dot-start" /><div><span className="muted small">Pickup · {whenLabel(pickup.date, pickup.time)}</span><b>{journey.origin.displayAddress}</b></div></div>
      <div><span className="dot dot-end" /><div><span className="muted small">Drop</span><b>{journey.destination.displayAddress}</b></div></div>
      {back && <div><span className="dot dot-start" /><div><span className="muted small">Return pickup · {whenLabel(back.date, back.time)}</span><b>{journey.destination.displayAddress} → {journey.origin.displayAddress}</b></div></div>}
    </div>
  );
}

function PolicyList({ title, items, className = '' }) {
  if (!items?.length) return null;
  return (
    <>
      <h4>{title}</h4>
      <ul className={`policy-list ${className}`}>{items.map((i) => <li key={i}>{i}</li>)}</ul>
    </>
  );
}

export default function TransferDetails() {
  const q = useQuery();
  const { state } = useLocation();
  const navigate = useNavigate();
  const search = parseTransferQuery(q);
  const resultsPath = `/transfers/results?${transferQueryString(search)}`;
  const [review, setReview] = useState(null);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  const offerId = q.offer;

  useEffect(() => {
    if (!offerId) return undefined;
    const controller = new AbortController();
    setError('');
    reviewTransfer(offerId, controller.signal)
      .then(setReview)
      .catch((e) => {
        if (e.name === 'AbortError') return;
        setError(e.status === 409 ? 'This price has expired. Search again to see current cabs and prices.' : e.message || 'We could not confirm the price of this cab.');
      });
    return () => controller.abort();
  }, [offerId, attempt]);

  if (!offerId) {
    return (
      <div className="container section empty">
        <h2>Cab not found</h2>
        <Link to="/transfers" className="btn btn-primary">Search transfers</Link>
      </div>
    );
  }

  // Until the review answers, show the cab from the results page (if we came from there).
  const offer = review?.offer || state?.offer;
  const journey = review?.journey || state?.journey;
  const policies = offer?.policies;
  const free = policies && freeCancellation(policies.cancellation);
  const continueToBook = () => navigate('/transfers/review', {
    state: { review, backTo: `/transfers/cab?${new URLSearchParams(q).toString()}` },
  });

  return (
    <>
      <FlowBand title={offer ? `${offer.vehicle.label} cab` : 'Cab details'} step={1} back={{ to: resultsPath, label: 'Back to cabs' }} />
      <div className="container details-layout">
        <div className="details-main">
          {offer && journey ? (
            <section className="panel">
              <div className="sum-media">
                <VehicleImage vehicle={offer.vehicle} />
                <div>
                  <span className="tag">{journeyLabel(journeyFromType(journey.journeyType))}{journey.tripType === 'RoundTrip' ? ' · Round trip' : ''}</span>
                  <h2 className="details-title">{offer.vehicle.label}</h2>
                  {offer.vehicle.similarModels && <span className="muted small">{offer.vehicle.similarModels}</span>}
                  <div className="transfer-meta">
                    {offer.vehicle.passengerCapacity != null && <span><Icon name="user" size={14} /> {offer.vehicle.passengerCapacity} seats</span>}
                    {offer.vehicle.luggageCapacity != null && <span><Icon name="bag" size={14} /> {offer.vehicle.luggageCapacity} bags</span>}
                    <span><Icon name="snow" size={14} /> AC</span>
                  </div>
                </div>
              </div>
              <RouteBox journey={journey} />
              <div className="kv-grid">
                {journey.distance && <div><span className="muted small">Distance</span><b>{journey.distance}</b></div>}
                {journey.durationMinutes > 0 && <div><span className="muted small">Drive time</span><b>about {durationLabel(journey.durationMinutes)}</b></div>}
                <div><span className="muted small">Passengers</span><b>{passengersLabel(journey.passengers)}</b></div>
              </div>
            </section>
          ) : !error && (
            <section className="panel" role="status"><span className="spinner sm" /> Loading cab…</section>
          )}

          {policies && (
            <section className="panel">
              <h3>What's included</h3>
              <ul className="policy-list check-list">
                {policies.inclusions.map((i) => <li key={i}>{i}</li>)}
                {policies.waitingTime && <li>Free waiting: {policies.waitingTime}</li>}
                {policies.meetAndGreet && <li>Meet & greet: {policies.meetAndGreet}</li>}
              </ul>
              <PolicyList title="Not included" items={policies.exclusions} className="cross-list" />
              <PolicyList title="Luggage" items={policies.baggage} />
              {policies.driverAssignment && <><h4>Driver details</h4><p className="muted">{policies.driverAssignment}</p></>}
            </section>
          )}

          {policies && (
            <section className="panel">
              <h3>Cancellation</h3>
              {free && <p className="text-primary"><Icon name="check" size={14} strokeWidth={2.5} /> {free}</p>}
              <ul className="policy-list">
                {cancellationLines(policies.cancellation).map((l) => <li key={l}>{l}</li>)}
                <li>The convenience fee is not refunded.</li>
              </ul>
              {policies.amendmentPolicy && <p className="muted small">{policies.amendmentPolicy}</p>}
              <PolicyList title="Terms" items={policies.terms} />
            </section>
          )}
        </div>

        <aside className="details-side">
          <div className="panel price-card">
            {error ? (
              <div className="api-message error" role="alert">
                <p>{error}</p>
                <div className="btn-row">
                  {!/expired/i.test(error) && <button type="button" className="btn btn-outline btn-sm" onClick={() => setAttempt(attempt + 1)}>Try again</button>}
                  <Link to={resultsPath} className="btn btn-primary btn-sm">Search again</Link>
                </div>
              </div>
            ) : (
              <>
                <span className="muted small">{review ? (journey?.tripType === 'RoundTrip' ? 'Round trip total, whole cab' : 'Total, whole cab') : 'Confirming price…'}</span>
                <b className="price big" aria-busy={!review}>{offer ? formatPrice(offer.price.total) : '—'}</b>
                {offer && (
                  <div className="fare-breakdown">
                    {/* Supplier leg fares don't always add up to the trip fare, so only the trip totals are shown. */}
                    <div className="summary-line"><span>Base fare{offer.legFares && ' (both ways)'}</span><span>{formatPrice(offer.price.baseFare)}</span></div>
                    <div className="summary-line"><span>Taxes</span><span>{formatPrice(offer.price.taxes)}</span></div>
                    {offer.price.convenienceFee > 0 && (
                      <div className="summary-line">
                        <span>Convenience fee{review?.convenienceFeeNote && <small className="muted fee-note">{review.convenienceFeeNote}</small>}</span>
                        <span>{formatPrice(offer.price.convenienceFee)}</span>
                      </div>
                    )}
                  </div>
                )}
                <button type="button" className="btn btn-primary block lg" onClick={continueToBook} disabled={!review}>
                  <Icon name="transfers" size={18} /> {review ? 'Continue' : 'Confirming price…'}
                </button>
                {review && <p className="muted small">Price held for 30 minutes.</p>}
              </>
            )}
          </div>
        </aside>
      </div>
    </>
  );
}
